import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const Thumb = ({ name, i, caption, onCaption, onRemove }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";
    api.get(`/admin/claim-photos/${name}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(""));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [name]);
  return (
    <div data-testid={`claim-photo-${i}`}>
      <div className="relative aspect-[4/3] overflow-hidden border border-stone bg-stone/30">
        {src ? <img src={src} alt={`Claim evidence ${i + 1}`} className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center text-xs text-charcoal/60">Photo {i + 1}</span>}
        <span className="absolute left-1.5 top-1.5 bg-forest px-1.5 text-xs text-white">{i + 1}</span>
        <button type="button" onClick={onRemove} aria-label={`Remove photo ${i + 1}`} className="absolute right-1.5 top-1.5 bg-white/90 p-1 text-red-800 transition-colors hover:bg-white" data-testid={`claim-photo-${i}-remove`}><X size={14} /></button>
      </div>
      <input type="text" maxLength={150} value={caption} onChange={(e) => onCaption(e.target.value)} placeholder="Caption (optional)" aria-label={`Caption for photo ${i + 1}`}
        className="field mt-1.5 !py-1.5 text-xs" data-testid={`claim-photo-${i}-caption`} />
    </div>
  );
};

// onChange(fn): fn({ photos, captions }) returns the next { photos, captions }; captions are index-aligned with photos.
export function ClaimPhotos({ photos, captions, onChange }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(0);
  const upload = async (files) => {
    const added = [];
    setBusy(files.length);
    for (const file of files) {
      const body = new FormData();
      body.append("file", file);
      try {
        const { data } = await api.post("/admin/claim-photos", body);
        added.push(data.path);
      } catch (err) {
        toast.error(`${file.name}: ${apiError(err)}`);
      }
      setBusy((n) => n - 1);
    }
    if (added.length) {
      onChange((s) => ({ photos: [...s.photos, ...added], captions: s.captions }));
      toast.success(`${added.length} photo${added.length === 1 ? "" : "s"} added. Save the claim to keep them.`);
    }
    if (input.current) input.current.value = "";
  };
  return (
    <fieldset className="mb-6" data-testid="claim-photos">
      <legend className="mb-3 text-sm font-semibold text-forest">Evidence photos ({photos.length}, printed in this order with their captions)</legend>
      {photos.length > 0 && (
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {photos.map((p, i) => (
            <Thumb key={p} name={p} i={i} caption={captions[i] ?? ""}
              onCaption={(v) => onChange((s) => ({ photos: s.photos, captions: s.photos.map((_, j) => (j === i ? v : s.captions[j] ?? "")) }))}
              onRemove={() => onChange((s) => ({ photos: s.photos.filter((_, j) => j !== i), captions: s.photos.map((_, j) => s.captions[j] ?? "").filter((_, j) => j !== i) }))} />
          ))}
        </div>
      )}
      <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="claim-photo-input" />
      <button type="button" disabled={busy > 0} onClick={() => input.current?.click()} className="btn inline-flex items-center gap-2 !py-2.5 text-sm" data-testid="claim-photo-upload">
        <ImagePlus size={16} /> {busy > 0 ? `Uploading ${busy}…` : "Upload photos"}
      </button>
    </fieldset>
  );
}
